import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BedDouble, Bath, Maximize, MapPin, Tag, Building2 } from "lucide-react";
import { api, imgSrc, eur, waLink, PHONE_DISPLAY } from "@/lib/api";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";
import { FadeIn } from "@/components/Reveal";

const PropiedadDetalle = () => {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [status, setStatus] = useState("loading");
  const [main, setMain] = useState(0);

  useEffect(() => {
    setStatus("loading");
    api
      .get(`/properties/${id}`)
      .then((r) => {
        setP(r.data);
        setMain(0);
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [id]);

  if (status === "loading")
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <p className="animate-pulse font-serif text-2xl text-gold">Cargando propiedad…</p>
      </div>
    );

  if (status === "notfound" || !p)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ink px-6 text-center">
        <p className="font-serif text-3xl text-ivory">Propiedad no encontrada</p>
        <Link to="/propiedades" data-testid="property-notfound-back" className="rounded-full border border-gold/40 px-6 py-3 text-sm font-semibold text-gold hover:bg-gold/10">
          Ver todas las propiedades
        </Link>
      </div>
    );

  const waText = `Hola Inmobiliaria Tecnohogar, me interesa la propiedad ${p.ref} — ${p.title} (${eur(p.price)}${
    p.operation === "alquiler" ? "/mes" : ""
  }). ¿Podemos organizar una visita?`;

  const specs = [
    { icon: BedDouble, label: "Habitaciones", value: p.bedrooms, testid: "property-detail-bedrooms" },
    { icon: Bath, label: "Baños", value: p.bathrooms, testid: "property-detail-bathrooms" },
    { icon: Maximize, label: "Superficie", value: p.area ? `${p.area} m²` : "—", testid: "property-detail-area" },
    { icon: Building2, label: "Tipo", value: p.property_type, testid: "property-detail-type" },
  ];

  return (
    <main className="mx-auto max-w-7xl px-6 pb-28 pt-28 md:px-8 md:pt-36">
      <Link to="/propiedades" data-testid="property-back-link" className="inline-flex items-center gap-2 text-sm text-stone2 transition-colors hover:text-gold">
        <ArrowLeft size={16} /> Volver a propiedades
      </Link>

      {/* Galería */}
      <FadeIn className="mt-8">
        <div className="overflow-hidden rounded-2xl border border-gold/25">
          <img
            src={imgSrc(p.images[main] || p.images[0])}
            alt={p.title}
            data-testid="property-gallery-main"
            className="aspect-[16/10] w-full object-cover"
          />
        </div>
        {p.images.length > 1 && (
          <div className="mt-4 grid grid-cols-4 gap-3 md:grid-cols-6">
            {p.images.map((im, i) => (
              <button
                key={i}
                onClick={() => setMain(i)}
                data-testid={`property-thumb-${i}`}
                className={`overflow-hidden rounded-lg border transition-all ${i === main ? "border-gold" : "border-gold/20 opacity-60 hover:opacity-100"}`}
              >
                <img src={imgSrc(im)} alt="" className="aspect-[4/3] w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </FadeIn>

      <div className="mt-12 grid gap-12 lg:grid-cols-[1fr_380px]">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] ${
                p.operation === "venta" ? "btn-gold-metallic text-ink" : "border border-gold/50 text-gold"
              }`}
              data-testid="property-detail-operation"
            >
              {p.operation}
            </span>
            <span className="text-xs uppercase tracking-[0.2em] text-gold-subtle">Ref. {p.ref}</span>
          </div>
          <h1 className="mt-4 font-serif text-3xl leading-tight text-ivory md:text-5xl">{p.title}</h1>
          <p className="mt-3 flex items-center gap-2 text-stone2">
            <MapPin size={16} className="text-gold-subtle" /> {p.location}
          </p>

          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {specs.map((s) => (
              <div key={s.label} className="rounded-xl border border-gold/15 bg-ink2/70 p-4 text-center" data-testid={s.testid}>
                <s.icon size={20} className="mx-auto text-gold" />
                <p className="mt-2 font-serif text-xl text-ivory">{s.value || "—"}</p>
                <p className="text-[11px] uppercase tracking-[0.16em] text-stone2">{s.label}</p>
              </div>
            ))}
          </div>

          {p.description && (
            <div className="mt-10">
              <h2 className="font-serif text-2xl text-gold">Descripción</h2>
              <p className="mt-4 leading-relaxed text-stone2">{p.description}</p>
            </div>
          )}

          {p.features && p.features.length > 0 && (
            <div className="mt-10">
              <h2 className="font-serif text-2xl text-gold">Características</h2>
              <div className="mt-4 flex flex-wrap gap-3">
                {p.features.map((f) => (
                  <span key={f} className="rounded-full border border-gold/25 bg-ink2/70 px-4 py-2 text-sm text-ivory/85">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <div className="rounded-2xl border border-gold/25 bg-ink2/80 p-7 shadow-[0_10px_30px_rgba(0,0,0,0.8)]">
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold-subtle">
              <Tag size={13} /> Precio
            </p>
            <p className="mt-2 font-serif text-4xl text-gold-metallic" data-testid="property-detail-price">
              {eur(p.price)}
              {p.operation === "alquiler" && <span className="text-base text-stone2"> /mes</span>}
            </p>
            <a
              href={waLink(waText)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="property-whatsapp-cta"
              className="mt-6 flex items-center justify-center gap-3 rounded-full bg-wa px-6 py-4 font-bold text-ink transition-transform hover:-translate-y-0.5"
            >
              <WhatsAppIcon className="h-5 w-5" /> Me interesa — WhatsApp
            </a>
            <p className="mt-4 text-center text-xs text-stone2">
              Te respondemos al momento: {PHONE_DISPLAY}. Visitas concertadas a tu medida.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PropiedadDetalle;
